import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './AdminStyles.css';

const AgencyRequestsManagement = () => {
  const { currentUser } = useAuth();
  const [agencyRequests, setAgencyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchAgencyRequests();
  }, []);

  const fetchAgencyRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      // The correct endpoint is /api/users/agency-requests
      let url = '/api/users/agency-requests';
      
      console.log('Fetching agency requests from:', url);
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch requests: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data || data.length === 0) {
        console.log('No agency requests found or empty response');
      } else {
        console.log('Agency requests fetched successfully:', data);
      }
      
      // Map the user data to the expected format for the component
      const formattedRequests = Array.isArray(data) ? data.map(user => ({
        _id: user._id,
        name: user.name || 'Unknown',
        email: user.email || 'No Email',
        phone: user.phone || '',
        agencyRequestDate: user.agencyRequestDate || new Date().toISOString(),
        agencyRequestMessage: user.agencyRequestMessage || '',
        agencyStatus: user.agencyStatus || 'pending'
      })) : [];
      
      setAgencyRequests(formattedRequests);
    } catch (err) {
      console.error('Error fetching agency requests:', err);
      setError(err.message || 'Failed to load agency requests');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (userId, action) => {
    try {
      setError(null);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      console.log(`Updating agency request status for user ${userId}: ${action}`);
      
      // The correct endpoint for approving/rejecting agency requests
      const response = await fetch(`/api/users/${userId}/agency-request/${action}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update status: ${response.statusText}`);
      }
      
      // Update the local state
      setAgencyRequests(prevRequests => 
        prevRequests.filter(req => req._id !== userId)
      );
      
      // Show success message
      setSuccessMessage(`Request ${action}d successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh the data
      fetchAgencyRequests();
    } catch (err) {
      console.error('Error updating agency request:', err);
      setError(err.message || 'Failed to update request status');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return <div className="admin-loading">Loading agency requests...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Agency Membership Requests</h2>
      </div>
      
      {error && <div className="admin-error">{error}</div>}
      {successMessage && <div className="admin-success">{successMessage}</div>}
      
      {agencyRequests.length === 0 ? (
        <div className="no-data-message">
          No pending agency requests found
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Contact</th>
                <th>Request Date</th>
                <th>Message</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agencyRequests.map(user => (
                <tr key={user._id} className={`status-${user.agencyStatus}`}>
                  <td>{user.name || 'Unknown'}</td>
                  <td>
                    <div>{user.email}</div>
                    <div>{user.phone}</div>
                  </td>
                  <td>{formatDate(user.agencyRequestDate)}</td>
                  <td>{user.agencyRequestMessage}</td>
                  <td>
                    <span className={`status-badge ${user.agencyStatus}`}>
                      {user.agencyStatus.charAt(0).toUpperCase() + user.agencyStatus.slice(1)}
                    </span>
                  </td>
                  <td>
                    {user.agencyStatus === 'pending' && (
                      <div className="action-buttons">
                        <button 
                          className="approve-btn"
                          onClick={() => handleStatusUpdate(user._id, 'approve')}
                        >
                          Approve
                        </button>
                        <button 
                          className="reject-btn"
                          onClick={() => handleStatusUpdate(user._id, 'reject')}
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AgencyRequestsManagement; 